import { isClockTime, minutesOfDay } from "../notifications/schedule";
import type { IsoDate } from "../primitives";
import type { Recurrence } from "../types/recurring";

/**
 * The maths of "every Saturday at 10:00" (#624), on calendar dates and minutes
 * of the day only — no time zone, no `Date.now()` — so the API, the fixture
 * adapter and the tests all give the same answer. The caller brings "now" as a
 * local date and minute (`localClock` in `notifications/schedule.ts`).
 *
 * A date is `YYYY-MM-DD`. Weekdays are ISO: 1 Monday to 7 Sunday.
 */

/** Never longer than this between two occurrences (a monthly one is at most 31 days). */
const SEARCH_DAYS = 62;

const WEEKDAY_NAMES = [
  "",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export const MAX_DUE_AFTER_DAYS = 30;

function parts(date: IsoDate): { y: number; m: number; d: number } {
  const [y, m, d] = date.split("-").map(Number);
  return { y: y ?? 1970, m: m ?? 1, d: d ?? 1 };
}

function utc(date: IsoDate): number {
  const { y, m, d } = parts(date);
  return Date.UTC(y, m - 1, d);
}

/** The date `days` after `date` (negative goes back). */
export function addDays(date: IsoDate, days: number): IsoDate {
  return new Date(utc(date) + days * 86_400_000).toISOString().slice(0, 10);
}

/** 1 Monday to 7 Sunday. */
export function isoWeekday(date: IsoDate): number {
  const day = new Date(utc(date)).getUTCDay();
  return day === 0 ? 7 : day;
}

export function daysInMonth(date: IsoDate): number {
  const { y, m } = parts(date);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Whether the rule has an occurrence on `date` (the time of day aside). */
export function occursOn(rule: Recurrence, date: IsoDate): boolean {
  switch (rule.kind) {
    case "daily":
      return true;
    case "weekly":
      return rule.weekdays.includes(isoWeekday(date));
    case "monthly": {
      if (rule.dayOfMonth === null) return false;
      // A day the month lacks (31 in April) means the month's last day.
      const target = Math.min(rule.dayOfMonth, daysInMonth(date));
      return parts(date).d === target;
    }
  }
}

/**
 * The most recent occurrence at or before `now`: today's, once its time has
 * come, otherwise the closest earlier day the rule lands on. Null for a rule
 * that never lands (no weekdays, no day of month).
 */
export function latestOccurrence(
  rule: Recurrence,
  now: { date: IsoDate; minutes: number },
): IsoDate | null {
  const at = minutesOfDay(rule.timeOfDay);
  for (let back = 0; back <= SEARCH_DAYS; back += 1) {
    const date = addDays(now.date, -back);
    if (!occursOn(rule, date)) continue;
    if (back === 0 && now.minutes < at) continue;
    return date;
  }
  return null;
}

/** The first occurrence strictly after `now`; null for a rule that never lands. */
export function nextOccurrence(
  rule: Recurrence,
  now: { date: IsoDate; minutes: number },
): IsoDate | null {
  const at = minutesOfDay(rule.timeOfDay);
  for (let ahead = 0; ahead <= SEARCH_DAYS; ahead += 1) {
    const date = addDays(now.date, ahead);
    if (!occursOn(rule, date)) continue;
    if (ahead === 0 && now.minutes >= at) continue;
    return date;
  }
  return null;
}

function ordinal(day: number): string {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${String(day)}th`;
  const suffix = ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[
    day % 10
  ];
  return `${String(day)}${suffix ?? "th"}`;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
}

/** "Every Saturday at 10:00", "Every Monday and Thursday at 09:00", "Monthly on the 5th at 09:00". */
export function describeRecurrence(rule: Recurrence): string {
  switch (rule.kind) {
    case "daily":
      return `Every day at ${rule.timeOfDay}`;
    case "weekly": {
      const names = [...new Set(rule.weekdays)]
        .sort((a, b) => a - b)
        .map((day) => WEEKDAY_NAMES[day] ?? "");
      if (names.length === 7) return `Every day at ${rule.timeOfDay}`;
      return `Every ${joinNames(names)} at ${rule.timeOfDay}`;
    }
    case "monthly": {
      const day = rule.dayOfMonth;
      if (day === null) return `Monthly at ${rule.timeOfDay}`;
      const which = day >= 31 ? "last day" : `${ordinal(day)}`;
      return `Monthly on the ${which} at ${rule.timeOfDay}`;
    }
  }
}

/**
 * What is wrong with a recurrence, field by field; empty when it is fine. The
 * server refuses a body with any of these, and the form shows the same words.
 */
export function recurrenceProblems(
  rule: Partial<Recurrence> & { dueAfterDays?: number },
): Record<string, string> {
  const problems: Record<string, string> = {};
  if (
    rule.kind !== "daily" &&
    rule.kind !== "weekly" &&
    rule.kind !== "monthly"
  ) {
    problems.kind = "Choose daily, weekly or monthly";
  }
  if (!isClockTime(rule.timeOfDay)) problems.timeOfDay = "Must be a time";
  if (rule.kind === "weekly") {
    const days = rule.weekdays;
    if (
      !Array.isArray(days) ||
      days.length === 0 ||
      !days.every((day) => Number.isInteger(day) && day >= 1 && day <= 7)
    ) {
      problems.weekdays = "Pick at least one day";
    }
  }
  if (rule.kind === "monthly") {
    const day = rule.dayOfMonth;
    if (
      typeof day !== "number" ||
      !Number.isInteger(day) ||
      day < 1 ||
      day > 31
    ) {
      problems.dayOfMonth = "Pick a day of the month, 1 to 31";
    }
  }
  if (rule.dueAfterDays !== undefined) {
    const days = rule.dueAfterDays;
    if (!Number.isInteger(days) || days < 0 || days > MAX_DUE_AFTER_DAYS) {
      problems.dueAfterDays = `Between 0 and ${String(MAX_DUE_AFTER_DAYS)} days`;
    }
  }
  return problems;
}

/** The task's due date for an occurrence: the same day, or `dueAfterDays` later. */
export function dueDateFor(occurrence: IsoDate, dueAfterDays: number): IsoDate {
  return addDays(occurrence, dueAfterDays);
}
